/**
 * auth-init.ts - Initialize authentication system
 *
 * This module ties together all authentication modules and initializes
 * the auth system when the page loads.
 */

import { initializeAuth } from './auth-state';
import { updateAuthUI, setupUserMenuHandler } from './auth-ui';
import { setupModalHandlers } from './auth-modals';

// Import handlers to ensure they're registered on window
import './auth-handlers';

// Import passkey module
import { initializePasskeyButton } from './passkey';

/**
 * Fetch available OAuth providers and show/hide buttons accordingly
 */
async function initializeOAuthButtons(): Promise<void> {
  try {
    const response = await fetch('/api/auth/oauth/providers');
    if (!response.ok) return;

    const providers = await response.json() as { google: boolean; lichess: boolean };

    // Show/hide Google buttons
    if (providers.google) {
      const googleLoginBtn = document.getElementById('google-login-btn');
      const googleRegisterBtn = document.getElementById('google-register-btn');
      if (googleLoginBtn) googleLoginBtn.style.display = 'flex';
      if (googleRegisterBtn) googleRegisterBtn.style.display = 'flex';
    }

    // Show/hide Lichess buttons
    if (providers.lichess) {
      const lichessLoginBtn = document.getElementById('lichess-login-btn');
      const lichessRegisterBtn = document.getElementById('lichess-register-btn');
      if (lichessLoginBtn) lichessLoginBtn.style.display = 'flex';
      if (lichessRegisterBtn) lichessRegisterBtn.style.display = 'flex';
    }

    // Show dividers if any OAuth provider is enabled
    if (providers.google || providers.lichess) {
      const dividerLogin = document.getElementById('oauth-divider-login');
      const dividerRegister = document.getElementById('oauth-divider-register');
      if (dividerLogin) dividerLogin.style.display = 'flex';
      if (dividerRegister) dividerRegister.style.display = 'flex';

      // Show SSO option in step 1 of registration wizard
      const ssoOption = document.getElementById('register-sso-option');
      if (ssoOption) ssoOption.style.display = 'block';
    }
  } catch (error) {
    console.error('Failed to fetch OAuth providers:', error);
  }
}

/**
 * Check for OAuth error in URL and show appropriate message
 */
function handleOAuthError(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const error = urlParams.get('error');

  if (error) {
    // Remove error from URL
    window.history.replaceState({}, '', window.location.pathname);

    // Get translated error message
    const errorMessages: Record<string, string> = {
      email_exists: 'This email is already registered with a different login method. Please use your existing password to log in.',
      oauth_denied: 'Authorization was denied. Please try again.',
      oauth_expired: 'Authorization session expired. Please try again.',
      oauth_no_email: 'Could not retrieve your email address. Please ensure your account has a verified email.',
      oauth_error: 'An error occurred during authentication. Please try again.',
      oauth_token_failed: 'Failed to complete authentication. Please try again.',
      oauth_user_failed: 'Could not retrieve your account information. Please try again.',
      oauth_not_configured: 'This login method is not configured.',
      oauth_failed: 'Authentication failed. Please try again.',
      oauth_invalid: 'Invalid authentication request. Please try again.'
    };

    const message = errorMessages[error] || 'An authentication error occurred.';

    // Show notification after a short delay to ensure the notification system is ready
    setTimeout(() => {
      if ((window as any).showNotification) {
        (window as any).showNotification(message, 'error');
      } else {
        alert(message);
      }
    }, 500);
  }
}

/**
 * Close all modals on page load to prevent stale modal states
 */
function closeAllModals(): void {
  const modals = document.querySelectorAll('.modal');
  let closedCount = 0;

  modals.forEach((modal) => {
    const el = modal as HTMLElement;
    // Check if modal was actually visible (potential stuck state)
    if (el.style.display === 'block' || el.style.display === 'flex') {
      console.warn('[Auth] Closing stuck modal:', el.id || el.className);
      closedCount++;
    }
    el.style.display = 'none';
  });

  // Also close any modal-backdrop elements that might be stuck
  const backdrops = document.querySelectorAll('.modal-backdrop');
  backdrops.forEach((backdrop) => {
    console.warn('[Auth] Removing stuck backdrop');
    backdrop.remove();
  });

  // If modals were stuck, it might indicate corrupted cache - clear it
  if (closedCount > 0) {
    console.warn('[Auth] Detected stuck modals, clearing partials cache');
    try {
      // Clear all versioned partials caches
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('partials-cache')) {
          localStorage.removeItem(key);
        }
      }
    } catch (e) {
      console.error('[Auth] Failed to clear cache:', e);
    }
  }
}

/**
 * Setup global Escape key handler to close any open modals
 * This provides a recovery mechanism if modals get stuck
 */
function setupEscapeKeyHandler(): void {
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      // Close all modals that are currently visible
      const modals = document.querySelectorAll('.modal');
      modals.forEach((modal) => {
        const el = modal as HTMLElement;
        if (el.style.display !== 'none' && el.style.display !== '') {
          el.style.display = 'none';
        }
      });
    }
  });
}

/**
 * Handle OAuth registration with custom username
 * Validates username and stores it before redirecting to OAuth provider
 */
function handleOAuthRegister(provider: 'google' | 'lichess'): void {
  const usernameInput = document.getElementById('register-username') as HTMLInputElement | null;
  const errorEl = document.getElementById('register-error');

  if (!usernameInput) return;

  const username = usernameInput.value.trim();

  // Validate username
  if (!username) {
    if (errorEl) {
      errorEl.textContent = window.t ? window.t('auth.usernameRequired') : 'Please enter a username';
    }
    usernameInput.focus();
    return;
  }

  if (username.length < 3) {
    if (errorEl) {
      errorEl.textContent = window.t ? window.t('auth.usernameTooShort') : 'Username must be at least 3 characters';
    }
    usernameInput.focus();
    return;
  }

  if (username.length > 50) {
    if (errorEl) {
      errorEl.textContent = window.t ? window.t('auth.usernameTooLong') : 'Username must be less than 50 characters';
    }
    usernameInput.focus();
    return;
  }

  // Clear any previous errors
  if (errorEl) errorEl.textContent = '';

  // Redirect to OAuth provider with username as query parameter
  window.location.href = `/api/auth/oauth/${provider}?username=${encodeURIComponent(username)}`;
}

// Expose handleOAuthRegister to window for onclick handlers
(window as any).handleOAuthRegister = handleOAuthRegister;

/**
 * Wizard step navigation functions
 */

function validateUsername(): boolean {
  const usernameInput = document.getElementById('register-username') as HTMLInputElement | null;
  const errorEl = document.getElementById('register-error');

  if (!usernameInput) return false;

  const username = usernameInput.value.trim();

  if (!username) {
    if (errorEl) {
      errorEl.textContent = window.t ? window.t('auth.usernameRequired') : 'Please enter a username';
    }
    usernameInput.focus();
    return false;
  }

  if (username.length < 3) {
    if (errorEl) {
      errorEl.textContent = window.t ? window.t('auth.usernameTooShort') : 'Username must be at least 3 characters';
    }
    usernameInput.focus();
    return false;
  }

  if (username.length > 50) {
    if (errorEl) {
      errorEl.textContent = window.t ? window.t('auth.usernameTooLong') : 'Username must be less than 50 characters';
    }
    usernameInput.focus();
    return false;
  }

  if (errorEl) errorEl.textContent = '';
  return true;
}

function showRegisterStep1(): void {
  const step1 = document.getElementById('register-step-1');
  const step2SSO = document.getElementById('register-step-2-sso');
  const step2Credentials = document.getElementById('register-step-2-credentials');
  const step3Password = document.getElementById('register-step-3-password');

  if (step1) step1.style.display = 'block';
  if (step2SSO) step2SSO.style.display = 'none';
  if (step2Credentials) step2Credentials.style.display = 'none';
  if (step3Password) step3Password.style.display = 'none';

  // Clear any error messages
  const errorEl = document.getElementById('register-error');
  if (errorEl) errorEl.textContent = '';
}

function showRegisterStep2SSO(): void {
  if (!validateUsername()) return;

  const step1 = document.getElementById('register-step-1');
  const step2SSO = document.getElementById('register-step-2-sso');
  const step2Credentials = document.getElementById('register-step-2-credentials');
  const step3Password = document.getElementById('register-step-3-password');

  if (step1) step1.style.display = 'none';
  if (step2SSO) step2SSO.style.display = 'block';
  if (step2Credentials) step2Credentials.style.display = 'none';
  if (step3Password) step3Password.style.display = 'none';
}

function showRegisterStep2Credentials(): void {
  if (!validateUsername()) return;

  const step1 = document.getElementById('register-step-1');
  const step2SSO = document.getElementById('register-step-2-sso');
  const step2Credentials = document.getElementById('register-step-2-credentials');
  const step3Password = document.getElementById('register-step-3-password');

  if (step1) step1.style.display = 'none';
  if (step2SSO) step2SSO.style.display = 'none';
  if (step2Credentials) step2Credentials.style.display = 'block';
  if (step3Password) step3Password.style.display = 'none';

  // Focus on email field
  const emailInput = document.getElementById('register-email') as HTMLInputElement | null;
  if (emailInput) {
    setTimeout(() => emailInput.focus(), 100);
  }
}

function showRegisterStep3Password(): void {
  const emailInput = document.getElementById('register-email') as HTMLInputElement | null;
  const errorEl = document.getElementById('register-error');

  if (!emailInput) return;

  const email = emailInput.value.trim();

  // Validate email
  if (!email) {
    if (errorEl) {
      errorEl.textContent = window.t ? window.t('auth.emailRequired') : 'Please enter your email';
    }
    emailInput.focus();
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    if (errorEl) {
      errorEl.textContent = window.t ? window.t('auth.invalidEmail') : 'Please enter a valid email';
    }
    emailInput.focus();
    return;
  }

  if (errorEl) errorEl.textContent = '';

  const step1 = document.getElementById('register-step-1');
  const step2SSO = document.getElementById('register-step-2-sso');
  const step2Credentials = document.getElementById('register-step-2-credentials');
  const step3Password = document.getElementById('register-step-3-password');

  if (step1) step1.style.display = 'none';
  if (step2SSO) step2SSO.style.display = 'none';
  if (step2Credentials) step2Credentials.style.display = 'none';
  if (step3Password) step3Password.style.display = 'block';

  // Focus on password field
  const passwordInput = document.getElementById('register-password') as HTMLInputElement | null;
  if (passwordInput) {
    setTimeout(() => passwordInput.focus(), 100);
  }
}

/**
 * Handle passkey signup from wizard - validates email first
 */
async function handlePasskeySignupWizard(): Promise<void> {
  const emailInput = document.getElementById('register-email') as HTMLInputElement | null;
  const errorEl = document.getElementById('register-error');

  if (!emailInput) return;

  const email = emailInput.value.trim();

  // Validate email
  if (!email) {
    if (errorEl) {
      errorEl.textContent = window.t ? window.t('auth.emailRequired') : 'Please enter your email';
    }
    emailInput.focus();
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    if (errorEl) {
      errorEl.textContent = window.t ? window.t('auth.invalidEmail') : 'Please enter a valid email';
    }
    emailInput.focus();
    return;
  }

  if (errorEl) errorEl.textContent = '';

  // Call the existing passkey signup handler
  if ((window as any).handlePasskeySignup) {
    await (window as any).handlePasskeySignup();
  }
}

// Expose wizard navigation functions to window
(window as any).showRegisterStep1 = showRegisterStep1;
(window as any).showRegisterStep2SSO = showRegisterStep2SSO;
(window as any).showRegisterStep2Credentials = showRegisterStep2Credentials;
(window as any).showRegisterStep3Password = showRegisterStep3Password;
(window as any).handlePasskeySignupWizard = handlePasskeySignupWizard;

/**
 * Username validation result
 */
interface UsernameValidationResult {
  valid: boolean;
  message: string;
  messageKey?: string;
}

/**
 * Validate username according to rules
 * - 3-50 characters
 * - Only letters, numbers, spaces, underscores, and hyphens
 */
function validateUsernameRealtime(username: string): UsernameValidationResult {
  if (!username) {
    return { valid: false, message: 'At least 3 characters', messageKey: 'auth.usernameHint' };
  }

  if (username.length < 3) {
    const remaining = 3 - username.length;
    const msg = remaining === 1 ? '1 more character needed' : `${remaining} more characters needed`;
    return { valid: false, message: msg, messageKey: 'auth.usernameNeedsMore' };
  }

  if (username.length > 50) {
    return { valid: false, message: 'Username must be less than 50 characters', messageKey: 'auth.usernameTooLong' };
  }

  // Check for invalid characters - allow letters, numbers, spaces, underscores, hyphens
  const validPattern = /^[a-zA-Z0-9_\- ]+$/;
  if (!validPattern.test(username)) {
    return {
      valid: false,
      message: 'Only letters, numbers, spaces, underscores, and hyphens allowed',
      messageKey: 'auth.usernameInvalidChars'
    };
  }

  return { valid: true, message: 'Username looks good!', messageKey: 'auth.usernameValid' };
}

/**
 * Setup real-time username validation
 */
function setupUsernameValidation(): void {
  const usernameInput = document.getElementById('register-username') as HTMLInputElement | null;
  const validationIcon = document.getElementById('username-validation-icon');
  const validationMessage = document.getElementById('username-validation-message');

  if (!usernameInput || !validationIcon || !validationMessage) return;

  usernameInput.addEventListener('input', () => {
    const username = usernameInput.value;
    const result = validateUsernameRealtime(username);

    // Update icon
    validationIcon.classList.remove('valid', 'invalid');
    if (username.length > 0) {
      validationIcon.classList.add('show', result.valid ? 'valid' : 'invalid');
    } else {
      validationIcon.classList.remove('show');
    }

    // Update input border
    usernameInput.classList.remove('valid', 'invalid');
    if (username.length > 0) {
      usernameInput.classList.add(result.valid ? 'valid' : 'invalid');
    }

    // Update message
    validationMessage.classList.remove('valid', 'invalid');
    if (username.length > 0) {
      validationMessage.classList.add(result.valid ? 'valid' : 'invalid');
    }

    // Get translated message if available
    let message = result.message;
    if (result.messageKey && window.i18n?.t) {
      const translated = window.i18n.t(result.messageKey);
      if (translated !== result.messageKey) {
        message = translated;
      }
    }
    validationMessage.textContent = message;
  });

  // Also validate on blur to show final state
  usernameInput.addEventListener('blur', () => {
    const username = usernameInput.value;
    if (username.length === 0) {
      // Reset to default state
      validationIcon.classList.remove('show', 'valid', 'invalid');
      usernameInput.classList.remove('valid', 'invalid');
      validationMessage.classList.remove('valid', 'invalid');
      validationMessage.textContent = window.i18n?.t ? window.i18n.t('auth.usernameHint') : 'At least 3 characters';
    }
  });
}

/**
 * Wait for partials to be loaded, then run callback
 */
function whenPartialsLoaded(callback: () => void): void {
  if (document.body.classList.contains('partials-loaded')) {
    callback();
  } else {
    const observer = new MutationObserver(() => {
      if (document.body.classList.contains('partials-loaded')) {
        observer.disconnect();
        callback();
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // Fallback timeout in case observer misses it
    setTimeout(() => {
      if (document.body.classList.contains('partials-loaded')) {
        observer.disconnect();
        callback();
      }
    }, 1000);
  }
}

/**
 * Initialize the authentication system
 * Sets up all auth-related functionality on page load
 */
function initAuth(): void {
  // Close any stuck modals from previous session
  // Run immediately for modals already in DOM
  closeAllModals();

  // Check for OAuth errors first
  handleOAuthError();

  // Setup global Escape key handler for modal recovery
  setupEscapeKeyHandler();

  // Wait for partials to load before initializing modal-dependent features
  whenPartialsLoaded(() => {
    // Close any modals that loaded from partials
    closeAllModals();

    // Setup modal click-outside handlers
    setupModalHandlers();

    // Setup user menu handler
    setupUserMenuHandler();

    // Initialize OAuth buttons (must wait for partial to load)
    initializeOAuthButtons();

    // Initialize passkey button (must wait for partial to load)
    initializePasskeyButton();

    // Setup real-time username validation
    setupUsernameValidation();
  });

  // Initialize auth state and update UI
  initializeAuth((user) => {
    updateAuthUI(user);

    // Check if we should auto-open registration or login modal
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('register') === 'true' && !user) {
      // Remove the parameter from URL
      window.history.replaceState({}, '', window.location.pathname);
      // Open registration modal after partials load
      whenPartialsLoaded(() => {
        setTimeout(() => {
          if (window.openAuthModal) {
            window.openAuthModal('register');
          }
        }, 100);
      });
    } else if (urlParams.get('login') === 'true' && !user) {
      // Remove the parameter from URL
      window.history.replaceState({}, '', window.location.pathname);
      // Open login modal after partials load (e.g., after email verification)
      whenPartialsLoaded(() => {
        setTimeout(() => {
          if (window.openAuthModal) {
            window.openAuthModal('login');
          }
        }, 100);
      });
    }
  });

  // Re-apply auth UI when language changes
  window.addEventListener('languageChanged', () => {
    const currentUser = window.getCurrentUser();
    updateAuthUI(currentUser);
  });
}

// Initialize auth when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAuth);
} else {
  // DOM already loaded
  initAuth();
}
